import type { StudyMissionSelectDto, StudyThreadSelectDto } from '@@types/api.types';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';

export type MeiaMissionDetailRow = {
  label: string;
  value: string;
};

export const sortMeiaMissionsByCreatedAt = (missions: StudyMissionSelectDto[]): StudyMissionSelectDto[] =>
  missions.toSorted((first, second) => {
    const createdAtDelta = new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime();
    if (createdAtDelta !== 0) return createdAtDelta;
    return first.id.localeCompare(second.id);
  });

export const getMeiaMissionAreaOfOperations = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.areaOfOperations ?? '';
};

export const getMeiaMissionAssumptionsConstraints = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.assumptionsConstraints ?? '';
};

export const getMeiaMissionAuthoritiesRoeEnvelope = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.authoritiesRoeEnvelope ?? '';
};

export const getMeiaMissionBlueForceComposition = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.blueForceComposition ?? '';
};

export const getMeiaMissionCommandersIntent = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.commandersIntent ?? mission?.narrative ?? '';
};

export const getMeiaMissionDesiredOutcome = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.desiredMissionOutcome ?? mission?.purposeStatement ?? '';
};

export const getMeiaMissionName = (mission?: StudyMissionSelectDto): string => {
  return mission?.name || mission?.purposeStatement || 'Untitled mission';
};

export const getMeiaMissionTimeWindow = (mission?: StudyMissionSelectDto): string => {
  return mission?.missionContext?.timeWindow ?? '';
};

export const getMeiaMissionDetailRows = (mission: StudyMissionSelectDto): MeiaMissionDetailRow[] => {
  const capabilityGapQuestions = (mission.capabilityGaps ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((capabilityGap) => capabilityGap.question.trim())
    .filter((question) => question.length > 0)
    .join('\n');
  const missionTypeCategory = mission.missionTypeCategory
    ? getStudyThreadKillChainLabel(mission.missionTypeCategory)
    : '';

  return [
    {
      label: 'Mission Type',
      value: missionTypeCategory,
    },
    {
      label: 'Capability Gaps',
      value: capabilityGapQuestions,
    },
    {
      label: 'Desired Mission Outcome',
      value: getMeiaMissionDesiredOutcome(mission),
    },
    {
      label: "Commander's Intent",
      value: getMeiaMissionCommandersIntent(mission),
    },
    {
      label: 'Area of Operations',
      value: getMeiaMissionAreaOfOperations(mission),
    },
    {
      label: 'Time Window',
      value: getMeiaMissionTimeWindow(mission),
    },
    {
      label: 'Blue Force Composition',
      value: getMeiaMissionBlueForceComposition(mission),
    },
    {
      label: 'Authorities / ROE Envelope',
      value: getMeiaMissionAuthoritiesRoeEnvelope(mission),
    },
    {
      label: 'Assumptions & Constraints',
      value: getMeiaMissionAssumptionsConstraints(mission),
    },
  ];
};

const MISSION_NAME_CODE_SEPARATOR = ' — ';

/** Name-based variant of `getMeiaMissionCode` for surfaces holding only the mission name (P4 snapshots). */
export const getMeiaMissionCodeFromName = (params: { index: number; name: string }): string => {
  const { index, name } = params;
  const [prefix] = name.split(MISSION_NAME_CODE_SEPARATOR);
  if (prefix && prefix !== name && prefix.trim().length <= 8) return prefix.trim();
  return `M${index + 1}`;
};

/** Short mission code for chips: the `M1` prefix when the name follows `M1 — Title`, else a positional code. */
export const getMeiaMissionCode = (params: { index: number; mission: StudyMissionSelectDto }): string =>
  getMeiaMissionCodeFromName({ index: params.index, name: params.mission.name });

/** Name-based variant of `getMeiaMissionTitle` for surfaces holding only the mission name (P4 snapshots). */
export const getMeiaMissionTitleFromName = (name: string): string => {
  const separatorIndex = name.indexOf(MISSION_NAME_CODE_SEPARATOR);
  if (separatorIndex === -1) return name;
  return name.slice(separatorIndex + MISSION_NAME_CODE_SEPARATOR.length) || name;
};

/** Mission title without the `M1 — ` code prefix, when present. */
export const getMeiaMissionTitle = (mission: StudyMissionSelectDto): string => {
  const title = getMeiaMissionTitleFromName(mission.name);
  return title || getMeiaMissionName(mission);
};

/** Compact threads label for study headers/listings, e.g. `cUAS + ISR` (prototype studyThreadsLabel). */
export const getMeiaStudyThreadsLabel = (threads: Array<Pick<StudyThreadSelectDto, 'killChainCategory'>>): string => {
  if (threads.length === 0) return '—';
  const labels = [...new Set(threads.map((thread) => getStudyThreadKillChainLabel(thread.killChainCategory)))];
  return labels.join(' + ');
};

/** Short mission summary for study headers (prototype studyMissionSummary). */
